// Teams (contracts/phase3.md "Web and admin"): search the teams, open one to see its
// members, week history, team_events and division. Editors reset an offensive name
// (confirm step, audited); admins disband a team (two confirm steps, audited).

import { teamInitials } from "@lumen/team-emblem";
import { TeamEmblem } from "@lumen/team-emblem/react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useDisbandTeam, useDivisions, useResetTeamName, useTeam, useTeams, type TeamStatusFilter } from "../api/queries";
import type { Divisions, TeamDetail, TeamListItem } from "../api/schemas";
import { useAuth, useCan } from "../auth/AuthContext";
import { Orb } from "../components/Orb";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Skeleton, Stat, TextInput } from "../components/ui";
import { WEB_ORIGIN } from "../env";
import { formatDateTime } from "../lib/dates";
import { fmtInt, shortId } from "../lib/format";
import { divisionAnchor, divisionText, eventNames, eventText, ordinal, OUTCOME_LABEL, OUTCOME_TONE, POLICY_LABEL, weekRange, ZONE_LABEL, ZONE_TONE } from "../lib/teams";
import styles from "./pages.module.css";
import t from "./teams.module.css";

const STATUS_TONE = { active: "success", dormant: "warning", disbanded: "muted" } as const;
const STATUSES = ["active", "dormant", "disbanded"] as const;

/** This week's live rank of every team in a division (the list API doesn't carry it). */
export function liveRows(d: Divisions | undefined) {
  const map = new Map<string, { tier: number; index: number; rank: number; of: number; score: number; sets: number | null; zone: Divisions["tiers"][number]["divisions"][number]["teams"][number]["zone"] }>();
  for (const tier of d?.tiers ?? []) {
    for (const div of tier.divisions) {
      for (const t of div.teams) map.set(t.teamId, { tier: tier.tier, index: div.index, rank: t.rank, of: div.teams.length, score: t.score, sets: t.sets, zone: t.zone });
    }
  }
  return map;
}

export function TeamsPage() {
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(params.get("q") ?? "");
  const q = params.get("q") ?? "";
  const rawStatus = params.get("status");
  const status: TeamStatusFilter = (STATUSES as readonly string[]).includes(rawStatus ?? "") ? (rawStatus as TeamStatusFilter) : null;
  const teams = useTeams(q, status);
  const live = liveRows(useDivisions(null).data);
  const navigate = useNavigate();
  const setStatus = (next: TeamStatusFilter) => setParams({ ...(q ? { q } : {}), ...(next ? { status: next } : {}) }, { replace: true });

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (draft === q) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (draft) next.set("q", draft);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    }, 250);
    return () => window.clearTimeout(id);
  }, [draft, q, setParams]);

  const items = teams.data?.items ?? [];
  return (
    <>
      <PageHeader title="Teams" meta={<span>Weekly-tournament teams, newest first: search by name, invite code or team id.</span>} />
      <div className={styles.filters}>
        <Field label="Search">
          <TextInput data-search value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Stackbrain, K7Q2MX…   ( / )" style={{ width: 340 }} autoFocus />
        </Field>
        <Segmented
          label="Status"
          value={status ?? "all"}
          onChange={(v) => setStatus(v === "all" ? null : v)}
          options={[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "dormant", label: "Dormant" },
            { value: "disbanded", label: "Disbanded" },
          ]}
        />
        <span className={styles.filtersEnd}>
          <span className="monoLabel">{teams.data ? `${items.length} ${q ? "matches" : "teams"}` : ""}</span>
          <Link to="/divisions" className={styles.back}>
            Divisions →
          </Link>
        </span>
      </div>

      {teams.isError ? (
        <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
      ) : (
        <div className={tableStyles.wrap} aria-busy={teams.isFetching}>
          <table className={tableStyles.table} data-testid="teams">
            <thead>
              <tr>
                <th>Team</th>
                <th>Code</th>
                <th>Division</th>
                <th data-align="right">Rank</th>
                <th data-align="right">Week score</th>
                <th>Roster</th>
                <th data-align="right">Streak</th>
                <th>Captain</th>
                <th>Joining</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {teams.isPending
                ? Array.from({ length: 10 }, (_, i) => (
                    <tr key={i}>
                      {[180, 60, 90, 30, 50, 90, 30, 90, 70, 60].map((w, j) => (
                        <td key={j}>
                          <Skeleton height={12} width={w} />
                        </td>
                      ))}
                    </tr>
                  ))
                : items.map((team) => <TeamRow key={team.id} team={team} rank={live.get(team.id)?.rank ?? team.rankThisWeek} onOpen={() => navigate(`/teams/${team.id}`)} />)}
            </tbody>
          </table>
          {!teams.isPending && items.length === 0 ? (
            <EmptyState title={q ? "No teams found" : "No teams yet"}>{q ? `Nothing matches “${q}”.` : "Teams players create in the app show up here."}</EmptyState>
          ) : null}
        </div>
      )}
    </>
  );
}

function TeamRow({ team, rank, onOpen }: { team: TeamListItem; rank: number | null; onOpen: () => void }) {
  return (
    <tr data-clickable data-team={team.id} data-status={team.status} onClick={(e) => !(e.target as HTMLElement).closest("a") && onOpen()}>
      <td>
        <Link to={`/teams/${team.id}`} className={t.teamCell}>
          <TeamEmblem emblem={team.emblem} size={22} />
          <span className={t.teamName}>{team.name}</span>
        </Link>
      </td>
      <td className={`${tableStyles.mono} ${tableStyles.dim}`}>{team.code ?? "—"}</td>
      <td className={tableStyles.mono}>{team.division ? divisionText(team.division) : <span className={tableStyles.faint}>—</span>}</td>
      <td data-align="right" className="mono">
        {rank ? ordinal(rank) : <span className={tableStyles.faint}>—</span>}
      </td>
      <td data-align="right" className="mono">
        {team.weekScore === null ? <span className={tableStyles.faint}>—</span> : fmtInt(team.weekScore)}
      </td>
      <td>
        <span className={t.roster}>
          <span className={t.rosterMeter} aria-hidden>
            <span style={{ width: `${Math.min(100, (team.memberCount / Math.max(1, team.cap)) * 100)}%` }} />
          </span>
          <span className="mono">
            {team.memberCount}/{team.cap}
          </span>
        </span>
      </td>
      <td data-align="right" className="mono">
        {team.streakWeeks ? `${team.streakWeeks} wk` : <span className={tableStyles.faint}>0</span>}
      </td>
      <td>
        {team.captain ? (
          <Link to={`/players/${team.captain.userId}`} className={`${tableStyles.id} ${tableStyles.dim}`}>
            {team.captain.name ?? shortId(team.captain.userId)}
          </Link>
        ) : (
          <span className={tableStyles.faint}>—</span>
        )}
      </td>
      <td className={tableStyles.dim}>{team.policy ? POLICY_LABEL[team.policy] : "—"}</td>
      <td>
        <Chip tone={STATUS_TONE[team.status]}>{team.status}</Chip>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------- detail

export function TeamDetailPage() {
  const { id = "" } = useParams();
  const q = useTeam(id);
  const d = q.data;
  const header = (
    <PageHeader
      title={
        d ? (
          <span className={t.title}>
            <TeamEmblem emblem={d.team.emblem} size={40} initials={teamInitials(d.team.name)} label={`${d.team.name} emblem`} />
            <span data-testid="team-name">{d.team.name}</span>
          </span>
        ) : (
          <span className="mono">{shortId(id, 13)}</span>
        )
      }
      meta={
        <>
          <Link to="/teams" className={styles.back}>
            ← Teams
          </Link>
          {d ? (
            <>
              <Chip tone={STATUS_TONE[d.team.status]}>{d.team.status}</Chip>
              {d.team.code ? <span className="mono">code {d.team.code}</span> : null}
            </>
          ) : null}
          <span className="mono">{id}</span>
        </>
      }
      actions={d ? <TeamActions detail={d} /> : null}
    />
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such team" : "Couldn't load this team"} />
      </>
    );
  const detail = q.data;
  return (
    <>
      {header}
      <div className={t.top}>
        <ProfileCard detail={detail} />
        <ThisWeekCard detail={detail} />
      </div>
      <Card title="Members this week" padded={false} actions={<Chip tone="muted">{`${detail.members.length} / ${detail.team.cap}`}</Chip>}>
        <MembersTable detail={detail} />
      </Card>
      <div className={t.bottom}>
        <Card title="Week history" padded={false}>
          <WeeksTable detail={detail} />
        </Card>
        <Card title="Events" actions={<Chip tone="muted">{String(detail.events.length)}</Chip>}>
          <EventsList detail={detail} />
        </Card>
      </div>
    </>
  );
}

function ProfileCard({ detail: { team } }: { detail: TeamDetail }) {
  const link = team.inviteUrl ?? (team.code && team.status !== "disbanded" ? `${WEB_ORIGIN}/t/${team.code}` : null);
  return (
    <Card title="Team">
      <dl className={styles.kv} data-testid="team-profile">
        <dt>Code</dt>
        <dd className="mono">{team.code ?? "—"}</dd>
        <dt>Link</dt>
        <dd className="mono">
          {link ? (
            <a href={link} target="_blank" rel="noreferrer" className={styles.back}>
              {link.replace(/^https?:\/\//, "")}
            </a>
          ) : (
            "—"
          )}
        </dd>
        <dt>Joining</dt>
        <dd>{team.policy ? POLICY_LABEL[team.policy] : "—"}</dd>
        <dt>Roster</dt>
        <dd className="mono">
          {team.memberCount} of {team.cap}
        </dd>
        <dt>Captain</dt>
        <dd>{team.captain ? <Link to={`/players/${team.captain.userId}`}>{team.captain.name ?? shortId(team.captain.userId, 13)}</Link> : "—"}</dd>
        <dt>Streak</dt>
        <dd className="mono">{team.streakWeeks ? `${team.streakWeeks} week${team.streakWeeks === 1 ? "" : "s"}` : "—"}</dd>
        <dt>Created</dt>
        <dd className="mono">{team.createdAt ? formatDateTime(team.createdAt) : "—"}</dd>
        {team.disbandedAt ? (
          <>
            <dt>Disbanded</dt>
            <dd className="mono">{formatDateTime(team.disbandedAt)}</dd>
          </>
        ) : null}
      </dl>
    </Card>
  );
}

function ThisWeekCard({ detail }: { detail: TeamDetail }) {
  const { team } = detail;
  // The live division tables carry this week's rank, score and zone.
  const divisions = useDivisions(null);
  const row = liveRows(divisions.data).get(team.id);
  const division = row ? { tier: row.tier, index: row.index } : team.division;
  const week = divisions.data?.week;
  return (
    <Card
      title={week ? `This week · ${week}` : "This week"}
      actions={
        division ? (
          <Link to={`/divisions#${divisionAnchor(division.tier, division.index)}`} className={styles.back}>
            Table →
          </Link>
        ) : null
      }
    >
      {!division ? (
        <p className={styles.faint}>{team.status === "active" ? "Not placed in a division yet." : `Not in a division (${team.status}).`}</p>
      ) : (
        <div className={t.week} data-testid="team-division">
          <div className={styles.statsRow}>
            <Stat label="Division" value={divisionText(division)} />
            <Stat label="Rank" value={row ? ordinal(row.rank) : "—"} sub={row ? `of ${row.of}` : undefined} />
            <Stat label="Score" value={fmtInt(row?.score ?? team.weekScore)} sub="of 500" />
            <Stat label="Sets done" value={row?.sets ?? detail.members.filter((m) => m.setDone).length} sub={`of ${detail.members.length}`} />
          </div>
          {row && row.zone && row.zone !== "safe" ? (
            <p className={t.zoneNote} data-zone={row.zone}>
              {row.zone === "promotion" ? "In the promotion zone (top 3)." : "In the relegation zone (16–18)."}
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function MembersTable({ detail }: { detail: TeamDetail }) {
  if (detail.members.length === 0) return <EmptyState title="No members">{detail.team.status === "disbanded" ? "The team is disbanded." : "Nobody is on this team."}</EmptyState>;
  return (
    <table className={tableStyles.table} data-testid="team-members">
      <thead>
        <tr>
          <th>Player</th>
          <th>Role</th>
          <th>Joined</th>
          <th>Set</th>
          <th data-align="right">Set score</th>
          <th>Counts</th>
        </tr>
      </thead>
      <tbody>
        {detail.members.map((m) => (
          <tr key={m.userId}>
            <td>
              <Link to={`/players/${m.userId}`} className={t.teamCell}>
                {m.orb !== null ? <Orb index={m.orb} size={18} label={`Orb ${m.orb}`} /> : null}
                <span>{m.handle ? `@${m.handle}` : m.name}</span>
              </Link>
            </td>
            <td>{m.role === "captain" ? <Chip tone="accent">captain</Chip> : <span className={tableStyles.dim}>member</span>}</td>
            <td className={tableStyles.mono}>
              {m.joinedAt ? formatDateTime(m.joinedAt) : "—"}
              {m.joinedMidWeek ? (
                <>
                  {" "}
                  <Chip tone="info">mid-week</Chip>
                </>
              ) : null}
            </td>
            <td>
              <span className={t.roster}>
                <span className={t.setMeter} aria-hidden>
                  <span style={{ width: `${((m.played ?? 0) / 10) * 100}%` }} />
                </span>
                <span className="mono">{m.played === null ? (m.setDone ? "done" : "—") : `${m.played}/10`}</span>
              </span>
            </td>
            <td data-align="right" className="mono">
              {m.setScore === null ? "—" : fmtInt(m.setScore)}
            </td>
            <td>{m.counting ? <Chip tone="teams">top 5</Chip> : null}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WeeksTable({ detail }: { detail: TeamDetail }) {
  if (detail.weeks.length === 0) return <EmptyState title="No closed weeks yet">The team's first week closes on Monday at 00:05 UTC.</EmptyState>;
  const best = detail.weeks.some((w) => w.bestSolver);
  return (
    <div className={t.scroll}>
      <table className={tableStyles.table} data-testid="team-weeks">
        <thead>
          <tr>
            <th>Week</th>
            <th>Division</th>
            <th data-align="right">Rank</th>
            <th data-align="right">Score</th>
            <th data-align="right">Sets</th>
            <th>Zone</th>
            <th>Outcome</th>
            {best ? <th>Best set</th> : null}
          </tr>
        </thead>
        <tbody>
          {detail.weeks.map((w, i) => (
            <tr key={w.week} data-week={w.week}>
              <td className={tableStyles.mono}>
                <Link to={`/divisions?week=${w.week}`}>{w.week}</Link> <span className={tableStyles.faint}>{weekRange(w.week)}</span>
              </td>
              <td className={tableStyles.mono}>{w.tier ? `D${w.tier}${w.index !== null ? ` · ${w.index}` : ""}` : "—"}</td>
              <td data-align="right" className="mono">
                {w.rank ? `${ordinal(w.rank)}${w.of ? ` / ${w.of}` : ""}` : "—"}
              </td>
              <td data-align="right" className="mono">
                {w.score === null ? "—" : fmtInt(w.score)}
              </td>
              <td data-align="right" className="mono">
                {w.sets ?? "—"}
              </td>
              <td>{w.zone && w.zone !== "safe" ? <Chip tone={ZONE_TONE[w.zone]}>{ZONE_LABEL[w.zone]}</Chip> : <span className={tableStyles.faint}>safe</span>}</td>
              <td>
                {w.outcome ? (
                  <span className={t.outcome}>
                    <Chip tone={OUTCOME_TONE[w.outcome]}>{OUTCOME_LABEL[w.outcome]}</Chip>
                    {w.newTier && w.tier && w.newTier !== w.tier ? <span className="mono">→ D{w.newTier}</span> : null}
                  </span>
                ) : i === 0 ? (
                  <Chip tone="info">in progress</Chip>
                ) : (
                  <Chip tone="warning" title="The week has ended; the close job hasn't run">not closed</Chip>
                )}
              </td>
              {best ? <td className={tableStyles.dim}>{w.bestSolver ? `${w.bestSolver.name} · ${w.bestSolver.setScore}` : "—"}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventsList({ detail }: { detail: TeamDetail }) {
  if (detail.events.length === 0) return <p className={styles.faint}>No events recorded.</p>;
  const names = new Map(detail.members.map((m) => [m.userId, m.handle ? `@${m.handle}` : m.name]));
  return (
    <ol className={t.events} data-testid="team-events">
      {detail.events.map((e) => {
        const { actor, subject } = eventNames(e, names);
        return (
          <li key={e.id} data-kind={e.kind} data-admin={e.admin || e.kind === "name_reset" || undefined}>
            <span className={t.eventDot} aria-hidden />
            <div className={t.eventBody}>
              <span className={t.eventText}>{eventText(e, names)}</span>
              <span className={t.eventMeta}>
                {actor && actor !== subject && !(["joined", "left"].includes(e.kind) && !subject) ? <span>{e.admin ? `admin ${actor}` : actor}</span> : null}
                {e.at ? <span className="mono">{formatDateTime(e.at)}</span> : null}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- actions

function TeamActions({ detail }: { detail: TeamDetail }) {
  const canReset = useCan("team.moderate");
  const canDisband = useCan("team.disband");
  const { admin } = useAuth();
  const [step, setStep] = useState<"none" | "reset" | "disband1" | "disband2">("none");
  const [typed, setTyped] = useState("");
  const reset = useResetTeamName();
  const disband = useDisbandTeam();
  const { team } = detail;
  if (team.status === "disbanded") return null;
  if (!canReset && !canDisband) {
    return (
      <span className={styles.readOnly} data-testid="read-only">
        Read-only · team moderation needs the editor role (you are {admin?.role})
      </span>
    );
  }
  const close = () => {
    setStep("none");
    setTyped("");
  };
  return (
    <>
      {canReset ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            reset.reset();
            setStep("reset");
          }}
        >
          Reset name
        </Button>
      ) : null}
      {canDisband ? (
        <Button
          size="sm"
          variant="danger"
          onClick={() => {
            disband.reset();
            setStep("disband1");
          }}
        >
          Disband
        </Button>
      ) : null}

      <ConfirmDialog
        open={step === "reset"}
        title="Reset this team's name?"
        confirmLabel="Reset name"
        tone="danger"
        busy={reset.isPending}
        error={reset.error}
        onCancel={close}
        onConfirm={() => reset.mutate(team.id, { onSuccess: close })}
      >
        <p className={styles.prose}>
          <span className="mono">{team.name}</span> is replaced with a generated “Team &lt;adjective&gt; &lt;noun&gt;” name everywhere: the
          division table, invite links and share cards. The captain can pick a new one in the app. Written to the audit log.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={step === "disband1"}
        title={`Disband ${team.name}?`}
        confirmLabel="Continue"
        tone="danger"
        onCancel={close}
        onConfirm={() => setStep("disband2")}
      >
        <div className={styles.prose}>
          <p>
            The team leaves {team.division ? divisionText(team.division) : "its division"} now and its {team.memberCount} member
            {team.memberCount === 1 ? " is" : "s are"} free to join another team. This week's score is lost and the invite code stops
            working.
          </p>
          <p className={styles.faint}>This can't be undone. Next, confirm with the team's name.</p>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={step === "disband2"}
        title="Really disband?"
        confirmLabel="Disband team"
        tone="danger"
        busy={disband.isPending}
        error={disband.error}
        onCancel={close}
        confirmDisabled={typed.trim() !== team.name}
        onConfirm={() => typed.trim() === team.name && disband.mutate(team.id, { onSuccess: close })}
      >
        <div className={styles.prose}>
          <p>
            Type <span className="mono">{team.name}</span> to disband it. Written to the audit log.
          </p>
          <Field label="Team name">
            <TextInput value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={team.name} aria-label="Type the team name to confirm" autoComplete="off" data-autofocus />
          </Field>
          {typed && typed.trim() !== team.name ? <p className={styles.fieldError}>That isn't the team's name.</p> : null}
        </div>
      </ConfirmDialog>
    </>
  );
}
